import { useEffect, useState } from 'react';

import { useSession } from '@/api/session';
import { Button, Card, Field, Notice, TextInput } from '@/components/ui';

/**
 * Sign-in. There is no "create account" path: accounts exist only through an
 * invitation an administrator issued.
 *
 * Administrators must complete TOTP; no session exists until they do.
 */
export function SignInPage() {
  const { signIn, verifyMfa } = useSession();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [enrolment, setEnrolment] = useState<{ uri: string; secret: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submitPassword(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const outcome = await signIn(email.trim(), password);
    setBusy(false);
    if (outcome.kind === 'mfa_required') {
      setChallengeToken(outcome.challengeToken);
      setEnrolment(outcome.enrolment ?? null);
    } else if (outcome.kind === 'error') {
      setError(outcome.message);
    }
  }

  async function submitCode(event: React.FormEvent) {
    event.preventDefault();
    if (!challengeToken) return;
    setBusy(true);
    setError(null);
    const outcome = await verifyMfa(challengeToken, code.trim());
    setBusy(false);
    if (outcome.kind === 'error') setError(outcome.message);
  }

  return (
    <div className="signin">
      <div className="signin__panel stack">
        <div style={{ textAlign: 'center' }}>
          <h1>Lumenest</h1>
          <p className="page__subtitle">Agency console</p>
        </div>

        {error ? (
          <Notice tone="urgent" title="Could not sign in">
            {error}
          </Notice>
        ) : null}

        {challengeToken ? (
          <Card>
            <form onSubmit={submitCode} className="stack">
              <h2>Two-factor authentication</h2>
              {enrolment ? (
                <Enrolment uri={enrolment.uri} secret={enrolment.secret} />
              ) : (
                <p className="meta">Enter the six-digit code from your authenticator app.</p>
              )}

              <Field label="Six-digit code">
                {(props) => (
                  <TextInput
                    {...props}
                    value={code}
                    onChange={(event) => setCode(event.target.value)}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="000000"
                    autoFocus
                  />
                )}
              </Field>

              <Button type="submit" size="lg" block busy={busy} disabled={code.trim().length < 6}>
                Verify and sign in
              </Button>
              <Button
                variant="ghost"
                block
                onClick={() => {
                  setChallengeToken(null);
                  setEnrolment(null);
                  setCode('');
                  setError(null);
                }}
              >
                Use a different account
              </Button>
            </form>
          </Card>
        ) : (
          <Card>
            <form onSubmit={submitPassword} className="stack">
              <Field label="Email">
                {(props) => (
                  <TextInput
                    {...props}
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    autoComplete="username"
                    placeholder="you@agency.org"
                    autoFocus
                  />
                )}
              </Field>

              <Field label="Password">
                {(props) => (
                  <TextInput
                    {...props}
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    autoComplete="current-password"
                  />
                )}
              </Field>

              <Button type="submit" size="lg" block busy={busy} disabled={!email.trim() || !password}>
                Sign in
              </Button>
            </form>
          </Card>
        )}

        <p className="meta" style={{ textAlign: 'center' }}>
          Accounts are created by invitation only. If anyone is in immediate danger, call 911 —
          this console is not an emergency service.
        </p>
      </div>
    </div>
  );
}

/**
 * First-time authenticator enrolment.
 *
 * The server has always sent a `provisioning_uri` — the otpauth:// URL every
 * authenticator app understands — and this screen used to ignore it and print
 * the raw base32 key instead. That meant enrolling was: read a long string off
 * a screen, open another app, find "enter a setup key", and type it in without
 * a mistake. It reliably took longer than the challenge token lasted, and the
 * resulting "challenge has expired" made it look as though the key was wrong.
 *
 * The key stays, below the code, because scanning fails on a desktop with no
 * second camera and because some password managers want the string.
 */
function Enrolment({ uri, secret }: { uri: string; secret: string }) {
  const [qr, setQr] = useState<string | null>(null);
  const [qrFailed, setQrFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Imported lazily: nobody enrolling is in a hurry, and everyone signing in
    // normally would otherwise pay for it in the bundle.
    import('qrcode')
      .then((mod) => mod.toDataURL(uri, { margin: 1, width: 220 }))
      .then((url) => { if (!cancelled) setQr(url); })
      .catch(() => { if (!cancelled) setQrFailed(true); });
    return () => { cancelled = true; };
  }, [uri]);

  return (
    <div className="enrolment">
      <Notice tone="attention" title="Set up your authenticator first">
        Administrators cannot sign in without an authenticator app. Scan this with
        Google Authenticator, 1Password, or whichever you use, then enter the code it
        shows.
      </Notice>

      {qr ? (
        <img className="enrolment__qr" src={qr} alt="QR code for your authenticator app" />
      ) : qrFailed ? null : (
        <p className="meta">Preparing the code…</p>
      )}

      <details className="enrolment__key">
        <summary>Can&rsquo;t scan it?</summary>
        <p className="meta">
          In your authenticator choose &ldquo;enter a setup key&rdquo; and paste this.
          It is shown once.
        </p>
        <code className="enrolment__secret">{secret}</code>
      </details>
    </div>
  );
}
